import { effect, Injectable, signal } from '@angular/core';
import { Contact, ContactDetail } from '../models/contact.model';

/**
 * Contact Store Service
 *
 * Servicio dedicado a la gestión de estado reactivo de contactos.
 * Mantiene el estado centralizado usando signals de Angular.
 *
 * */
@Injectable({
  providedIn: 'root',
})
export class ContactStoreService {
  private readonly _selectedContact = signal<Contact | null>(null);
  private readonly _selectedContactDetail = signal<ContactDetail | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _hasMore = signal<boolean>(true);
  private readonly _totlaContacts = signal<number>(0);

  private readonly _error = signal<string | null>(null);
  private readonly _success = signal<string>('');

  readonly selectedContact = this._selectedContact.asReadonly();
  readonly selectedContactDetail = this._selectedContactDetail.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly hasMore = this._hasMore.asReadonly();
  readonly success = this._success.asReadonly();
  readonly totalContacts = this._totlaContacts.asReadonly();

  constructor() {
    effect(() => {
      if(this.success() !== '') {
        const timeoutId = setTimeout(() => {
          this._success.set('');
          clearTimeout(timeoutId);
        }, 4000);
      }
    });

    effect(() => {
      if (this.error() !== null) {
        const timeoutId = setTimeout(() => {
          this._error.set(null);
          clearTimeout(timeoutId);
        }, 5000);
      }
    });
  }

  /**
   * Establece el contacto seleccionado (versión simple)
   */
  setSelectedContact(contact: Contact | null): void {
    this._selectedContact.set(contact);
  }

  /**
   * Establece el contacto detallado seleccionado
   */
  setSelectedContactDetail(contactDetail: ContactDetail | null): void {
    this._selectedContactDetail.set(contactDetail);
  }

  /**
   * Limpia el contacto seleccionado (ambas versiones)
   */
  clearSelectedContact(): void {
    this._selectedContact.set(null);
    this._selectedContactDetail.set(null);
  }

  /**
   * Establece el estado de carga
   */
  setLoading(isLoading: boolean): void {
    this._isLoading.set(isLoading);
  }

  /**
   * Establece si hay más contactos para cargar (paginación)
   */
  setHasMore(hasMore: boolean): void {
    this._hasMore.set(hasMore);
  }

  /**
   * Establece un mensaje de error
   */
  setError(error: string | null): void {
    this._error.set(error);
  }

  /**
   * Establece un mensaje de éxito
   */
  setSucces(message: string): void {
    this._success.set(message);
  }

  /*
   * Establece el total de contactos (para paginación o estadísticas)
   */
  setTotalContacts(total: number): void {
    this._totlaContacts.set(total);
  }

  /**
   * Limpia el mensaje de error
   */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Resetea todo el estado a valores iniciales
   */
  reset(): void {
    this.clearSelectedContact();
    this._isLoading.set(false);
    this._error.set(null);
    // this._success.set('');
  }
}
